import { useSyncExternalStore } from 'react';
import {
  emptyMetaCache,
  putWatchProgressEntry,
  readMetaCache,
  WATCH_PROGRESS_VERSION,
  type WatchProgressEntry,
  type WatchProgressStore,
} from '@visious/core';

/**
 * Where 4b's playheads live — the storage half of `playback/progress.ts`, and the third near-copy of
 * this file's shape after `channelMetaStore.ts` and `videoMetaStore.ts`.
 *
 * Near-copy rather than an abstraction over the other two, for the reason `videoMetaStore` gives: what
 * the three share is the *policy*, and that is one implementation in `cache/metaCache.ts`. What they do
 * not share is a storage key, a schema version and a subscription, and a factory over those three
 * constants would make one bump of `WATCH_PROGRESS_VERSION` evict channel avatars as well.
 *
 * **This one holds something the other two do not: a record of what this browser has watched.** That
 * changes three decisions, and each of them is a decision rather than an inherited default.
 *
 * - **Not per instance, and not dropped on sign-out.** A position is a fact about a *video*, so it
 *   survives an instance switch exactly as a cached title does. And signing out of an instance is not
 *   the same act as forgetting what you watched here — the credential is per origin and per instance
 *   (R9), while this store is the local half of a feature that works signed out at all. What makes that
 *   defensible rather than sloppy is that forgetting is offered explicitly: the settings page has a
 *   count and a clear, next to the one for remembered titles.
 * - **Not under the `visious.token.` prefix**, which is a rule with teeth: R9's `clearOtherInstances()`
 *   deletes every key with that prefix that is not the current instance's, on every boot
 *   (docs/specs/auth.md).
 * - **Nothing here is written on a render.** `touchMetaCache` is never called — see
 *   `playback/progress.ts` for why this store's LRU field is written equal to its creation time — so a
 *   feed page drawing forty progress bars performs zero writes. The video metadata cache had to guard
 *   against exactly this with an identity check; here it is structural.
 */

const STORAGE_KEY = 'visious.progress';

/** Read once and kept in memory: this is on the render path of every listing page. */
let store: WatchProgressStore = load();
const listeners = new Set<() => void>();

function load(): WatchProgressStore {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (raw === null || raw === undefined) {
      return emptyMetaCache<WatchProgressEntry>(WATCH_PROGRESS_VERSION);
    }
    // R4's division of labour: `JSON.parse` out here, core validates the parsed value.
    return readMetaCache<WatchProgressEntry>(
      JSON.parse(raw) as WatchProgressStore | null,
      WATCH_PROGRESS_VERSION,
    );
  } catch {
    // Blocked storage, a quota error, or a blob some other version wrote.
    return emptyMetaCache<WatchProgressEntry>(WATCH_PROGRESS_VERSION);
  }
}

function persist(next: WatchProgressStore): void {
  store = next;
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /*
     * Swallowed, and the in-memory value kept — but note this is the one store where losing the write
     * loses *data* rather than a cached copy of something re-fetchable. There is nothing better to do
     * about it here: core has already bounded what we are trying to store (2000 entries, ~300 KB), so a
     * quota failure means the origin is full of something else, and failing playback over a playhead
     * would be the wrong trade.
     */
  }
  for (const listener of listeners) listener();
}

/** Read the whole store — what the resume decision and every progress bar look up against. */
export function readWatchProgress(): WatchProgressStore {
  return store;
}

/**
 * One entry, raw — **without** core's expiry reading applied.
 *
 * That distinction is the reason this exists beside `watchProgressMark`: a mark is what is *usable*, so
 * an entry past its year is no mark at all. A write needs the opposite — it needs to know whether a row
 * for this id exists at any age, because `progressSample` only lets a position below the watch threshold
 * through when it is *updating* one (`playback/progress.ts`). Handing it a mark would make an expired
 * entry look absent and the first write would then be refused until the threshold was reached again.
 */
export function readWatchProgressEntry(videoId: string): WatchProgressEntry | null {
  return store.entries.find((entry) => entry.id === videoId) ?? null;
}

export function putWatchProgress(entry: WatchProgressEntry): void {
  const next = putWatchProgressEntry(store, entry);
  if (next !== store) persist(next);
}

/** How many positions are held — the number the settings page puts next to "forget". */
export function watchProgressCount(): number {
  return store.entries.length;
}

/**
 * Forget every stored position. A privacy control rather than a maintenance one, and unlike
 * `clearVideoMeta` it is not recoverable at the cost of a round of requests: what is deleted here
 * cannot be re-derived from the instance, because the instance never had it.
 */
export function clearWatchProgress(): void {
  persist(emptyMetaCache<WatchProgressEntry>(WATCH_PROGRESS_VERSION));
}

/**
 * One `storage` listener for the whole document, installed on the first subscriber and never taken
 * down. It used to be one per *subscriber*, which made a single cross-tab write cost one full re-read
 * and one round of notifications **per subscriber** — see `platform/settings.ts`, where a grid made
 * that quadratic and froze the tab for seconds.
 */
let watchingOtherTabs = false;

function watchOtherTabs(): void {
  if (watchingOtherTabs) return;
  watchingOtherTabs = true;
  /*
   * Another tab is a real case: two tabs on this origin share the key, so without this a feed left open
   * in one tab keeps showing no bar for the video that was just watched in the other. `event.key === null`
   * is a `localStorage.clear()`, which counts as "everything changed".
   */
  globalThis.addEventListener('storage', (event: StorageEvent) => {
    if (event.key === null || event.key === STORAGE_KEY) {
      // One `load()` for the event rather than one per subscriber, which is what this key could least
      // afford: the blob holds up to 2000 positions and every read of it is a synchronous parse.
      store = load();
      for (const listener of listeners) listener();
    }
  });
}

function subscribe(onChange: () => void): () => void {
  watchOtherTabs();
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

function getSnapshot(): WatchProgressStore {
  return store;
}

export function useWatchProgressStore(): WatchProgressStore {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
